import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Plus, Search, Edit2, Trash2, Eye, EyeOff, ChevronDown, 
  X, Save, AlertCircle, CheckCircle, HelpCircle, ArrowUpDown
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category options
const CATEGORIES = [
  { value: 'formation', label: 'Business Formation' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'state', label: 'State Selection' },
  { value: 'banking', label: 'Banking' },
  { value: 'tax', label: 'Tax' },
  { value: 'payment', label: 'Payment' },
  { value: 'documents', label: 'Documents' },
  { value: 'investment', label: 'Investment Documents' },
  { value: 'ai', label: 'AI Documents' },
  { value: 'general', label: 'General' },
];

// Target audience options
const TARGET_AUDIENCES = [
  { value: 'all', label: 'All' },
  { value: 'india_founders', label: 'India Founders' },
  { value: 'uk_founders', label: 'UK Founders' },
  { value: 'eu_founders', label: 'EU Founders' },
  { value: 'us_residents', label: 'US Residents' },
  { value: 'startups', label: 'Startups' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'freelancers', label: 'Freelancers' },
];

// FAQ Form Component (Drawer)
const FAQFormDrawer = ({ isOpen, onClose, faq, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    target_audience: ['all'],
    related_service_slug: '',
    display_order: 100,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (faq) {
      setFormData({
        question: faq.question || '',
        answer: faq.answer || '',
        category: faq.category || 'general',
        target_audience: faq.target_audience || ['all'],
        related_service_slug: faq.related_service_slug || '',
        display_order: faq.display_order || 100,
      });
    } else {
      setFormData({
        question: '',
        answer: '',
        category: 'general',
        target_audience: ['all'],
        related_service_slug: '',
        display_order: 100,
      });
    }
    setErrors({});
  }, [faq, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAudienceToggle = (audience) => {
    setFormData(prev => {
      const current = prev.target_audience || [];
      if (current.includes(audience)) {
        return { ...prev, target_audience: current.filter(a => a !== audience) };
      } else {
        return { ...prev, target_audience: [...current, audience] };
      }
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.question.trim()) newErrors.question = 'Question is required';
    if (!formData.answer.trim()) newErrors.answer = 'Answer is required';
    if (!formData.category) newErrors.category = 'Category is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="relative w-full max-w-xl bg-white shadow-xl overflow-y-auto" data-testid="faq-form-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {faq ? 'Edit FAQ' : 'Add New FAQ'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Question */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Question <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.question}
              onChange={(e) => handleChange('question', e.target.value)}
              rows={2}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                errors.question ? 'border-red-300' : 'border-slate-200'
              }`}
              placeholder="What question are you answering?"
              data-testid="faq-question-input"
            />
            {errors.question && (
              <p className="mt-1 text-sm text-red-500">{errors.question}</p>
            )}
          </div>

          {/* Answer */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Answer <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.answer}
              onChange={(e) => handleChange('answer', e.target.value)}
              rows={6}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                errors.answer ? 'border-red-300' : 'border-slate-200'
              }`}
              placeholder="Provide a clear, helpful answer..."
              data-testid="faq-answer-input"
            />
            {errors.answer && (
              <p className="mt-1 text-sm text-red-500">{errors.answer}</p>
            )}
            <p className="mt-1 text-xs text-slate-400">
              {formData.answer.split(/\s+/).filter(w => w).length} words
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="faq-category-select"
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Target Audience */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Target Audience
            </label>
            <div className="flex flex-wrap gap-2">
              {TARGET_AUDIENCES.map(audience => (
                <button
                  key={audience.value}
                  type="button"
                  onClick={() => handleAudienceToggle(audience.value)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    formData.target_audience?.includes(audience.value)
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                      : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                  }`}
                  data-testid={`audience-${audience.value}`}
                >
                  {audience.label}
                </button>
              ))}
            </div>
          </div>

          {/* Related Service */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Related Service Slug (optional)
            </label>
            <input
              type="text"
              value={formData.related_service_slug}
              onChange={(e) => handleChange('related_service_slug', e.target.value)}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="e.g., llc, c-corp, ein"
              data-testid="faq-service-input"
            />
          </div>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Display Order
            </label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 100)}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              min={1}
              data-testid="faq-order-input"
            />
            <p className="mt-1 text-xs text-slate-400">
              Lower numbers appear first
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="faq-save-btn"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  {faq ? 'Update FAQ' : 'Create FAQ'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, faqQuestion, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="delete-confirm-modal">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete FAQ?</h3>
          <p className="text-slate-600 text-sm mb-6">
            Are you sure you want to delete this FAQ? This action cannot be undone.
          </p>
          <p className="text-slate-500 text-sm italic mb-6 bg-slate-50 p-3 rounded">
            "{faqQuestion?.substring(0, 80)}..."
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
              data-testid="confirm-delete-btn"
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const SuperAdminFAQ = () => {
  const { token } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, faq: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchFAQs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/faq-items`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 500 }
      });
      setFaqs(response.data.items || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError(extractErrorMessage(err, 'Failed to load FAQs'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFAQs();
  }, [fetchFAQs]);

  // Filter FAQs
  const filteredFaqs = faqs.filter(faq => {
    if (categoryFilter !== 'all' && faq.category !== categoryFilter) return false;
    if (statusFilter === 'published' && !faq.is_published) return false;
    if (statusFilter === 'draft' && faq.is_published) return false;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      if (!faq.question.toLowerCase().includes(query) && !faq.answer.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });

  // CRUD Operations
  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      if (editingFaq) {
        await axios.put(`${API}/admin/faq-items/${editingFaq.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('FAQ updated successfully');
      } else {
        await axios.post(`${API}/admin/faq-items`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('FAQ created successfully');
      }
      setIsDrawerOpen(false);
      setEditingFaq(null);
      fetchFAQs();
    } catch (err) {
      console.error('Error saving FAQ:', err);
      showToast(err.response?.data?.detail || 'Failed to save FAQ', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.faq) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/admin/faq-items/${deleteModal.faq.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('FAQ deleted successfully');
      setDeleteModal({ isOpen: false, faq: null });
      fetchFAQs();
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      showToast(err.response?.data?.detail || 'Failed to delete FAQ', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTogglePublish = async (faq) => {
    try {
      const endpoint = faq.is_published 
        ? `${API}/admin/faq-items/${faq.id}/unpublish`
        : `${API}/admin/faq-items/${faq.id}/publish`;
      
      await axios.put(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast(faq.is_published ? 'FAQ unpublished' : 'FAQ published');
      fetchFAQs();
    } catch (err) {
      console.error('Error toggling publish:', err);
      showToast(err.response?.data?.detail || 'Failed to update FAQ', 'error');
    }
  };

  const openEditDrawer = (faq) => {
    setEditingFaq(faq);
    setIsDrawerOpen(true);
  };

  const openNewDrawer = () => {
    setEditingFaq(null);
    setIsDrawerOpen(true);
  };

  // Stats
  const stats = {
    total: faqs.length,
    published: faqs.filter(f => f.is_published).length,
    draft: faqs.filter(f => !f.is_published).length,
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="superadmin-faq-page">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
        }`}>
          {toast.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">FAQ Management</h1>
              <p className="text-slate-500 mt-1">Manage frequently asked questions</p>
            </div>
            <button
              onClick={openNewDrawer}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
              data-testid="add-faq-btn"
            >
              <Plus size={20} />
              Add FAQ
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-500">Total FAQs</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-emerald-600">{stats.published}</p>
              <p className="text-sm text-slate-500">Published</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-amber-600">{stats.draft}</p>
              <p className="text-sm text-slate-500">Drafts</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search FAQs..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="faq-search"
              />
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="category-filter"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">Loading FAQs...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-500 mb-4">{error}</p>
            <button 
              onClick={fetchFAQs}
              className="text-emerald-600 font-medium hover:text-emerald-700"
            >
              Try again
            </button>
          </div>
        ) : filteredFaqs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl">
            <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No FAQs found</h3>
            <p className="text-slate-500 mb-6">
              {searchQuery || categoryFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by adding your first FAQ'}
            </p>
            {!searchQuery && categoryFilter === 'all' && statusFilter === 'all' && (
              <button
                onClick={openNewDrawer}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
              >
                <Plus size={18} />
                Add First FAQ
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full" data-testid="faq-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">
                    <button className="flex items-center gap-1 hover:text-slate-900">
                      Question <ArrowUpDown size={14} />
                    </button>
                  </th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-slate-600 w-32">Category</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600 w-24">Order</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600 w-24">Status</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600 w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredFaqs.map((faq) => (
                  <tr key={faq.id} className="hover:bg-slate-50" data-testid={`faq-row-${faq.id}`}>
                    <td className="px-6 py-4">
                      <p className="text-slate-900 font-medium line-clamp-2">{faq.question}</p>
                      <p className="text-slate-400 text-sm mt-1 line-clamp-1">{faq.answer}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                        {faq.category}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center text-slate-500">
                      {faq.display_order}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => handleTogglePublish(faq)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          faq.is_published
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        }`}
                        data-testid={`toggle-publish-${faq.id}`}
                      >
                        {faq.is_published ? (
                          <>
                            <Eye size={12} /> Published
                          </>
                        ) : (
                          <>
                            <EyeOff size={12} /> Draft
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditDrawer(faq)}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Edit"
                          data-testid={`edit-faq-${faq.id}`}
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ isOpen: true, faq })}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                          data-testid={`delete-faq-${faq.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Results count */}
        {!loading && !error && filteredFaqs.length > 0 && (
          <p className="text-sm text-slate-500 mt-4">
            Showing {filteredFaqs.length} of {faqs.length} FAQs
          </p>
        )}
      </div>

      {/* Form Drawer */}
      <FAQFormDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditingFaq(null);
        }}
        faq={editingFaq}
        onSave={handleSave}
        isLoading={isSaving}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, faq: null })}
        onConfirm={handleDelete}
        faqQuestion={deleteModal.faq?.question}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default SuperAdminFAQ;
